import { useEffect, useRef } from "react";
import { api, isInFlight, type Generation } from "../api";

/**
 * Polls the generations that have not finished yet, and stops the moment none are left
 * (IMPLEMENTATION_PLAN.md §11.3).
 *
 * Polling is a decision, not a shortcut. A provider call takes 10–120 seconds and there are two
 * users on this system, so there is no load argument for SSE — and a poll is refresh-safe by
 * construction, has no reconnection semantics to get wrong, survives a laptop lid closing, and
 * is a tenth of the code. A query library would add the same loop plus a cache we do not need.
 */
const POLL_MS = 1500;

/** The server caps a poll at 50 ids; more in-flight generations than that is not a real state. */
const MAX_IDS = 50;

export function useGenerationPolling(
  generations: Generation[],
  onUpdate: (updated: Generation[]) => void,
): void {
  // Held in a ref so a caller passing an inline callback does not restart the timer every render.
  const onUpdateRef = useRef(onUpdate);
  useEffect(() => {
    onUpdateRef.current = onUpdate;
  }, [onUpdate]);

  // A string key, so the effect restarts only when the *set* of in-flight ids changes — not on
  // every re-render that rebuilds the array.
  const inFlightKey = generations
    .filter((generation) => isInFlight(generation.status))
    .slice(0, MAX_IDS)
    .map((generation) => generation.id)
    .join(",");

  useEffect(() => {
    if (inFlightKey === "") return;

    const ids = inFlightKey.split(",");
    const controller = new AbortController();
    let requestOpen = false;

    const tick = async () => {
      // Never overlap: a slow response must not queue a second request behind it, or a stalled
      // network turns into a pile-up of identical calls.
      if (requestOpen) return;
      requestOpen = true;
      try {
        const updated = await api.pollGenerations(ids, controller.signal);
        if (!controller.signal.aborted) onUpdateRef.current(updated);
      } catch {
        // A dropped poll is not worth showing anyone: the next tick asks again, and the
        // generation itself is safe on the server either way.
      } finally {
        requestOpen = false;
      }
    };

    const timer = window.setInterval(() => void tick(), POLL_MS);

    return () => {
      window.clearInterval(timer);
      controller.abort();
    };
  }, [inFlightKey]);
}
